import React, { useState } from 'react';
import {
    inviteProjectMember, removeProjectMember, addProjectMember,
    getProjectInvitations, cancelProjectInvitation, resendProjectInvitation,
    type ProjectMember, type ProjectInvitation,
} from '../../../packages/api-client/pm-api';
import { getUsers, type User } from '../../../packages/api-client/pm-api';
import toast from 'react-hot-toast';
import { apiClient, getErrorMessage } from '../../../packages/api-client/index';
import './MembersSection.css';
import { Plus, User as UserIcon, Mail } from 'lucide-react';
import { Badge, ButtonTabs, Dialog, TabPanel, Table, type Column } from '../../../packages/ui';
import { useHasPermission } from '../../../packages/permission-guard';

// State for the Members section and the Add Member modal (which the page
// header also opens). Called unconditionally by ProjectDetailPage so it
// outlives tab switches; the page's initial load fills setInvitations.
export function useMembersSection(
    id: string | undefined,
    canRestrict: boolean,
    setMembers: React.Dispatch<React.SetStateAction<ProjectMember[]>>,
) {
    const [invitations, setInvitations] = useState<ProjectInvitation[]>([]);
    const [invBusy, setInvBusy] = useState<string | null>(null);
    const [showInvite, setShowInvite] = useState(false);
    const [inviteMode, setInviteMode] = useState<'pick' | 'email'>('pick');
    const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' });
    const [pickUserId, setPickUserId] = useState('');
    const [pickRole, setPickRole] = useState('member');
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [inviting, setInviting] = useState(false);
    const [inviteMsg, setInviteMsg] = useState('');
    const [inviteProjRestricted, setInviteProjRestricted] = useState(false);
    const [inviteTaskRestricted, setInviteTaskRestricted] = useState(false);

    function openInvite() {
        setInviteMsg('');
        setInviteProjRestricted(false);
        setInviteTaskRestricted(false);
        if (allUsers.length === 0) getUsers().then(setAllUsers).catch(() => { });
        setShowInvite(true);
    }

    async function handleInvite(e: React.FormEvent) {
        e.preventDefault();
        if (!id) return;
        setInviting(true); setInviteMsg('');
        try {
            if (inviteMode === 'pick') {
                if (!pickUserId) { setInviteMsg('Please select a user'); setInviting(false); return; }
                const m = await addProjectMember(id, pickUserId, pickRole);
                setMembers(prev => [...prev.filter(x => x.user_id !== m.user_id), m]);
                setInviteMsg('Member added ✓');
                setPickUserId('');
            } else if (canRestrict && (inviteProjRestricted || inviteTaskRestricted)) {
                // Restricted invite → route through the org-invite, which creates
                // the (new) user with the restriction flags AND drops them into
                // this project in one step. Reuses the backend invite flow.
                await apiClient.post('/user-invitations', {
                    email: inviteForm.email,
                    project_id: id,
                    project_role: inviteForm.role,
                    is_project_restricted: inviteProjRestricted,
                    is_task_restricted: inviteTaskRestricted,
                });
                setInviteMsg(`Restricted invitation sent to ${inviteForm.email} ✓`);
                setInviteForm({ email: '', role: 'member' });
                setInviteProjRestricted(false);
                setInviteTaskRestricted(false);
            } else {
                await inviteProjectMember(id, inviteForm.email, inviteForm.role);
                setInviteMsg(`Invitation sent to ${inviteForm.email} ✓`);
                setInviteForm({ email: '', role: 'member' });
            }
            // Refresh the pending-invitations list (email invites add a row).
            if (id && inviteMode === 'email') getProjectInvitations(id).then(setInvitations).catch(() => {});
        } catch { setInviteMsg('Failed — user may already be a member'); }
        finally { setInviting(false); }
    }

    async function handleRemoveMember(userId: string) {
        if (!id || !confirm('Remove this member?')) return;
        try { await removeProjectMember(id, userId); } catch (e) { toast.error(getErrorMessage(e)); return; }
        setMembers(m => m.filter(x => x.user_id !== userId));
    }

    async function handleResendInvite(invId: string) {
        if (!id) return;
        setInvBusy(invId);
        try {
            await resendProjectInvitation(id, invId);
            getProjectInvitations(id).then(setInvitations).catch(() => {});
        } catch { /* surface nothing — list stays as-is */ }
        finally { setInvBusy(null); }
    }

    async function handleCancelInvite(invId: string) {
        if (!id || !confirm('Cancel this invitation? The link will stop working.')) return;
        setInvBusy(invId);
        try {
            await cancelProjectInvitation(id, invId);
            setInvitations(prev => prev.filter(x => x.id !== invId));
        } catch { /* ignore */ }
        finally { setInvBusy(null); }
    }

    return {
        invitations, setInvitations, invBusy,
        showInvite, setShowInvite, inviteMode, setInviteMode, inviteForm, setInviteForm,
        pickUserId, setPickUserId, pickRole, setPickRole, allUsers, inviting, inviteMsg,
        inviteProjRestricted, setInviteProjRestricted, inviteTaskRestricted, setInviteTaskRestricted,
        openInvite, handleInvite, handleRemoveMember, handleResendInvite, handleCancelInvite,
    };
}

type Row<T> = T & Record<string, unknown>;
const memberName = (m: ProjectMember) => `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.email;

export function MembersSection({ members, state }: {
    members: ProjectMember[];
    state: ReturnType<typeof useMembersSection>;
}) {
    const { invitations, invBusy, openInvite, handleRemoveMember, handleResendInvite, handleCancelInvite } = state;
    // UI gates only; the backend enforces project_members:* on every request.
    const canAdd = useHasPermission('project_members', 'create');
    const canRemove = useHasPermission('project_members', 'delete');
    const memberCols: Column<Row<ProjectMember>>[] = [
        { key: 'name', header: 'Name', rowHeader: true, render: memberName },
        { key: 'email', header: 'Email' },
        { key: 'role', header: 'Project role', render: (m) => <Badge>{m.role}</Badge> },
        {
            key: 'actions', header: 'Actions', render: (m) => m.role === 'owner' || !canRemove ? null : (
                <button type="button" className="btn btn-ghost btn-sm members-danger" aria-label={`Remove ${memberName(m)}`}
                    onClick={() => handleRemoveMember(m.user_id)}>Remove</button>
            ),
        },
    ];
    const inviteCols: Column<Row<ProjectInvitation>>[] = [
        { key: 'email', header: 'Email', rowHeader: true },
        { key: 'role', header: 'Project role', render: (i) => <Badge>{i.role}</Badge> },
        {
            key: 'status', header: 'Status', render: (i) => new Date(i.expires_at).getTime() < Date.now()
                ? <Badge variant="danger">Expired {new Date(i.expires_at).toLocaleDateString()}</Badge>
                : <>Invited{i.inviter_name ? ` by ${i.inviter_name}` : ''} · expires {new Date(i.expires_at).toLocaleDateString()}</>,
        },
        {
            key: 'actions', header: 'Actions', render: (i) => (
                <span className="members-actions">
                    <button type="button" className="btn btn-ghost btn-sm" disabled={invBusy === i.id} onClick={() => handleResendInvite(i.id)}>Resend</button>
                    <button type="button" className="btn btn-ghost btn-sm members-danger" disabled={invBusy === i.id} onClick={() => handleCancelInvite(i.id)}>Cancel</button>
                </span>
            ),
        },
    ];
    return (
        <div className="members-section">
            <section className="pd-panel pd-panel--block" aria-labelledby="members-h">
                <div className="members-header">
                    <div>
                        <span className="pd-eyebrow">Project membership</span>
                        <h2 id="members-h" className="members-title">People with project access</h2>
                    </div>
                    {canAdd && <button type="button" className="btn btn-primary btn-sm members-add" onClick={openInvite}><Plus size={14} aria-hidden="true" /> Add member</button>}
                </div>
                <Table caption="People with project access" hideCaption columns={memberCols} data={members as Row<ProjectMember>[]}
                    rowKey={(m) => m.id} emptyMessage="No members yet." />
                {invitations.length > 0 && (
                    <>
                        <h3 className="members-title members-subtitle">Pending invitations ({invitations.length})</h3>
                        <Table caption="Pending invitations" hideCaption columns={inviteCols}
                            data={invitations as Row<ProjectInvitation>[]} rowKey={(i) => i.id} />
                    </>
                )}
                {!canAdd && <p>Workspace admins manage project membership and task sharing.</p>}
            </section>
            <p className="pd-note">Assigning a task and granting access are separate actions.</p>
        </div>
    );
}

export function InviteMemberModal({ members, canRestrict, state }: {
    members: ProjectMember[];
    canRestrict: boolean;
    state: ReturnType<typeof useMembersSection>;
}) {
    const {
        showInvite, setShowInvite, inviteMode, setInviteMode, inviteForm, setInviteForm,
        pickUserId, setPickUserId, pickRole, setPickRole, allUsers, inviting, inviteMsg,
        inviteProjRestricted, setInviteProjRestricted, inviteTaskRestricted, setInviteTaskRestricted,
        handleInvite,
    } = state;
    const roleSelect = (id: string, value: string, onChange: (v: string) => void) => (
        <div className="form-group">
            <label className="form-label" htmlFor={id}>Role</label>
            <select id={id} className="form-input" value={value} onChange={e => onChange(e.target.value)}>
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
            </select>
        </div>
    );
    return (
        <Dialog open={showInvite} onClose={() => setShowInvite(false)} title="Add team member" closeOnBackdrop={false}>
            <ButtonTabs label="How to add" idPrefix="add-member" value={inviteMode}
                onChange={(v) => setInviteMode(v as 'pick' | 'email')}
                items={[
                    { value: 'pick', label: <><UserIcon size={14} aria-hidden="true" /> Existing user</> },
                    { value: 'email', label: <><Mail size={14} aria-hidden="true" /> Invite by email</> },
                ]} />
            <TabPanel idPrefix="add-member" value={inviteMode}>
                <form onSubmit={handleInvite} className="members-form">
                    {inviteMode === 'pick' ? (<>
                        <div className="form-group">
                            <label className="form-label" htmlFor="add-member-user">Select user</label>
                            <select id="add-member-user" className="form-input" value={pickUserId} onChange={e => setPickUserId(e.target.value)}>
                                <option value="">— Choose a user —</option>
                                {allUsers.filter(u => !members.some(m => m.user_id === u.id)).map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.first_name || u.last_name ? `${u.first_name} ${u.last_name}`.trim() : u.email} ({u.email})
                                    </option>
                                ))}
                            </select>
                            {allUsers.length === 0 && <p className="members-hint">No users to add yet — invite people from People &amp; access first.</p>}
                        </div>
                        {roleSelect('add-member-role', pickRole, setPickRole)}
                    </>) : (<>
                        <div className="form-group">
                            <label className="form-label" htmlFor="add-member-email">Email address</label>
                            <input id="add-member-email" className="form-input" type="email" placeholder="teammate@company.com" required
                                value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} />
                        </div>
                        {roleSelect('add-member-invite-role', inviteForm.role, (v) => setInviteForm(f => ({ ...f, role: v })))}
                        {canRestrict && (
                            <fieldset className="members-fieldset">
                                <legend className="form-label">Access scope (optional)</legend>
                                <label className="members-check">
                                    <input type="checkbox" checked={inviteProjRestricted} onChange={e => setInviteProjRestricted(e.target.checked)} />
                                    Project-restricted — only sees projects they belong to
                                </label>
                                <label className="members-check">
                                    <input type="checkbox" checked={inviteTaskRestricted} onChange={e => setInviteTaskRestricted(e.target.checked)} />
                                    Task-restricted — only sees tasks assigned to or created by them
                                </label>
                                <p className="members-hint">
                                    Restricted invites create a new scoped user and add them to this project. For an existing user, set this in People &amp; access → Edit.
                                </p>
                            </fieldset>
                        )}
                    </>)}
                    {inviteMsg && <p role="status" className={inviteMsg.includes('✓') ? 'members-ok' : 'members-error'}>{inviteMsg}</p>}
                    <div className="members-footer">
                        <button className="btn btn-secondary btn-md" type="button" onClick={() => setShowInvite(false)}>Cancel</button>
                        <button className="btn btn-primary btn-md" type="submit" disabled={inviting}>
                            {inviting ? 'Adding…' : inviteMode === 'pick' ? 'Add member' : 'Send invitation'}
                        </button>
                    </div>
                </form>
            </TabPanel>
        </Dialog>
    );
}
